import React from 'react';
import { useTranslation } from 'localization';
import './DeployBehaviorMolecule.scss';

export type ConflictStrategy = 'skip' | 'overwrite' | 'merge' | 'fail';

export interface DeployBehaviorMoleculeProps {
  conflictStrategy: ConflictStrategy;
  rollback: boolean;
  cascade: boolean;
  className?: string;
  header?: React.ReactNode;
}

// Per-row visual tone. `info` is the default neutral treatment;
// `success` is reserved for affirmative positives (rollback allowed).
type RowTone = 'info' | 'success';

interface BehaviorRowProps {
  icon: string;
  label: string;
  description: string;
  tone: RowTone;
}

const BehaviorRow: React.FC<BehaviorRowProps> = ({
  icon,
  label,
  description,
  tone,
}) => (
  <li
    className={`marketplace-deploy-behavior__row marketplace-deploy-behavior__row--${tone}`}
  >
    <span className="marketplace-deploy-behavior__icon-box" aria-hidden="true">
      <i className={`marketplace-deploy-behavior__icon pi ${icon}`} />
    </span>
    <div className="marketplace-deploy-behavior__row-body">
      <span className="marketplace-deploy-behavior__label">{label}</span>
      <span
        className="marketplace-deploy-behavior__separator"
        aria-hidden="true"
      >
        &nbsp;&mdash;&nbsp;
      </span>
      <span className="marketplace-deploy-behavior__description">
        {description}
      </span>
    </div>
  </li>
);

const DeployBehaviorMolecule: React.FC<DeployBehaviorMoleculeProps> = ({
  conflictStrategy,
  rollback,
  cascade,
  className = '',
  header,
}) => {
  const { t } = useTranslation();
  const rootClasses = ['marketplace-deploy-behavior', className]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={rootClasses}>
      {header ?? (
        <header className="marketplace-deploy-behavior__header">
          {t('marketplace.approachDetail.overview.deployBehavior.sectionTitle')}
        </header>
      )}
      <ul className="marketplace-deploy-behavior__list">
        <BehaviorRow
          icon="pi-arrows-h"
          tone="info"
          label={t(
            'marketplace.approachDetail.overview.deployBehavior.conflictStrategy',
          )}
          description={t(
            `marketplace.approachDetail.overview.deployBehavior.conflictStrategies.${conflictStrategy}`,
          )}
        />
        <BehaviorRow
          icon={rollback ? 'pi-replay' : 'pi-history'}
          // Rollback-allowed is the only affirmative positive state in this
          // section — tints the card green to signal safety.
          tone={rollback ? 'success' : 'info'}
          label={t(
            'marketplace.approachDetail.overview.deployBehavior.rollback',
          )}
          description={t(
            rollback
              ? 'marketplace.approachDetail.overview.deployBehavior.rollbackEnabled'
              : 'marketplace.approachDetail.overview.deployBehavior.rollbackDisabled',
          )}
        />
        <BehaviorRow
          icon={cascade ? 'pi-sitemap' : 'pi-circle'}
          tone="info"
          label={t(
            'marketplace.approachDetail.overview.deployBehavior.cascade',
          )}
          description={t(
            cascade
              ? 'marketplace.approachDetail.overview.deployBehavior.cascadeEnabled'
              : 'marketplace.approachDetail.overview.deployBehavior.cascadeDisabled',
          )}
        />
      </ul>
    </section>
  );
};

DeployBehaviorMolecule.displayName = 'DeployBehaviorMolecule';

export default DeployBehaviorMolecule;
